import * as amplitude from '@amplitude/analytics-browser';
import { getConsent } from '@/lib/consent';
import Logger from '@/lib/logger';
import { getAmplitudeApiKey, getAmplitudeServerUrl, isAmplitudeConfigured } from './client';
import { buildAnonymousAmplitudeEvent, buildAnonymousAmplitudePayload } from './anonymousEvent';
import type { AnonymousAmplitudeIdentity } from './anonymousIdentity';
import { applyAnonymousAmplitudePrivacy } from './anonymousPrivacy';
import { createAmplitudeMemoryStorage } from './memoryStorage';
import { drainPendingAmplitudeItems, enqueuePendingAmplitudeItem } from './pendingEvents';
import type { PendingAmplitudeEvent } from './pendingEvents';

const logger = new Logger('AnonymousAmplitude');
const anonymousAmplitude = amplitude.createInstance();
const ANONYMOUS_PRIVACY_PLUGIN = 'ft-anonymous-amplitude-privacy';

const eventStorage = createAmplitudeMemoryStorage<amplitude.Types.Event[]>();
let initialized = false;
let enabled = false;
let privacyPluginAdded = false;
let featureFlagEnabled = false;

const sendAnonymousAmplitudeEvent = ({
  eventName,
  eventProperties,
  time,
  adTrackingConsent,
}: PendingAmplitudeEvent): void => {
  if (adTrackingConsent) {
    return;
  }

  const anonymousEvent = buildAnonymousAmplitudePayload(eventName, eventProperties);
  if (!anonymousEvent) {
    return;
  }

  anonymousAmplitude
    .track(anonymousEvent.eventName, anonymousEvent.properties, { time })
    .promise.catch((error) => {
      logger.warn('[AnonymousAmplitude] Failed to track event', error);
    });
};

const flushPendingAnonymousEvents = (): void => {
  drainPendingAmplitudeItems().forEach((item) => {
    if (item.adTrackingConsent) {
      return;
    }

    if (item.type === 'event') {
      sendAnonymousAmplitudeEvent(item);
    }
  });
};

const shouldQueueAnonymousEvents = (): boolean => {
  const consent = getConsent();
  return (
    featureFlagEnabled &&
    typeof window !== 'undefined' &&
    isAmplitudeConfigured() &&
    consent.hydrated &&
    consent.isGdprRegion &&
    !consent.allowed
  );
};

const addPrivacyPlugin = async (): Promise<void> => {
  if (privacyPluginAdded) {
    return;
  }

  const plugin: amplitude.Types.EnrichmentPlugin = {
    name: ANONYMOUS_PRIVACY_PLUGIN,
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => applyAnonymousAmplitudePrivacy(event),
  };

  await anonymousAmplitude.add(plugin).promise;
  privacyPluginAdded = true;
};

const runEnable = async (
  { deviceId, sessionId }: AnonymousAmplitudeIdentity,
  shouldActivate: () => boolean
): Promise<boolean> => {
  if (!initialized) {
    const serverUrl = getAmplitudeServerUrl();

    await anonymousAmplitude.init(getAmplitudeApiKey(), {
      autocapture: false,
      cookieOptions: {
        upgrade: false,
      },
      deviceId,
      fetchRemoteConfig: false,
      identityStorage: 'none',
      minIdLength: 1,
      optOut: true,
      sessionId,
      storageProvider: eventStorage,
      trackingOptions: {
        ipAddress: false,
        language: false,
        platform: false,
      },
      ...(serverUrl && { serverUrl }),
    }).promise;
    initialized = true;
  }

  await addPrivacyPlugin();

  if (!shouldActivate()) {
    anonymousAmplitude.setOptOut(true);
    await eventStorage.reset();
    return false;
  }

  anonymousAmplitude.setUserId(undefined);
  anonymousAmplitude.setDeviceId(deviceId);
  anonymousAmplitude.setSessionId(sessionId);
  anonymousAmplitude.setOptOut(false);
  enabled = true;
  flushPendingAnonymousEvents();
  return true;
};

export const isAnonymousAmplitudeEnabled = (): boolean => enabled;

export const setAnonymousAmplitudeFeatureFlagEnabled = (nextEnabled: boolean): void => {
  featureFlagEnabled = nextEnabled;
};

export const optOutAnonymousAmplitudeImmediately = (): void => {
  enabled = false;
  if (initialized) {
    anonymousAmplitude.setOptOut(true);
  }
};

export const enableAnonymousAmplitude = async (
  identity: AnonymousAmplitudeIdentity,
  shouldActivate: () => boolean
): Promise<boolean> => {
  enabled = false;

  if (!featureFlagEnabled || typeof window === 'undefined' || !isAmplitudeConfigured()) {
    return false;
  }

  return runEnable(identity, shouldActivate);
};

export const disableAnonymousAmplitude = async (): Promise<void> => {
  enabled = false;

  if (!initialized) {
    return;
  }

  try {
    await anonymousAmplitude.flush().promise;
  } catch (error) {
    logger.warn('[AnonymousAmplitude] Failed to flush while disabling', error);
  } finally {
    anonymousAmplitude.setOptOut(true);
    await eventStorage.reset();
  }
};

export const trackAnonymousAmplitudeEvent = (
  eventName: string,
  properties: Record<string, unknown>
): void => {
  if (!featureFlagEnabled) {
    return;
  }

  const shouldSendImmediately = enabled;
  if (!shouldSendImmediately && !shouldQueueAnonymousEvents()) {
    return;
  }

  const event = buildAnonymousAmplitudeEvent(eventName, properties);
  if (!event) {
    return;
  }

  const pendingEvent: PendingAmplitudeEvent = {
    type: 'event',
    eventName: event.eventName,
    eventProperties: event.properties,
    time: event.time ?? Date.now(),
    adTrackingConsent: false,
  };

  if (shouldSendImmediately) {
    sendAnonymousAmplitudeEvent(pendingEvent);
  } else {
    enqueuePendingAmplitudeItem(pendingEvent);
  }
};
